import { storefrontRoutes } from '../../../front/src/storefrontRoutes.js'

// First path segment. `/apricot` is allowed; `/api` and `/api/docs` are not.
const RESERVED_SEGMENTS = new Set(['api', 'admin'])

const SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export { storefrontRoutes }

export function normalizeStaticRoute(input) {
	if (typeof input !== 'string') return { ok: false, error: 'Enter a route.' }
	let raw = input.trim()
	if (!raw) return { ok: false, error: 'Enter a route.' }
	if (/[?#\\]/.test(raw)) return { ok: false, error: 'Routes cannot include ?, #, or backslashes.' }

	raw = raw.toLowerCase().replace(/[\s_]+/g, '-')
	if (!raw.startsWith('/')) raw = `/${raw}`
	raw = raw.replace(/\/{2,}/g, '/')
	if (raw.length > 1) raw = raw.replace(/\/+$/, '')
	if (raw === '/') return { ok: false, error: 'Choose a route other than /.' }

	const segments = []
	for (const segment of raw.slice(1).split('/')) {
		const part = segment.replace(/-+/g, '-').replace(/^-+|-+$/g, '')
		if (!part || !SEGMENT.test(part)) {
			return { ok: false, error: 'Routes can only use letters, numbers, and hyphens, like /wholesale-policy.' }
		}
		segments.push(part)
	}

	if (segments.join('/').length > 180) return { ok: false, error: 'That route is too long.' }
	if (RESERVED_SEGMENTS.has(segments[0])) {
		return { ok: false, error: `Routes starting with /${segments[0]} are reserved.` }
	}

	return { ok: true, path: `/${segments.join('/')}`, slug: segments.join('/') }
}

function joinRoutePath(parent, child) {
	if (child == null) return parent || '/'
	if (child.startsWith('/')) return child || '/'
	if (!parent || parent === '/') return child ? `/${child}` : '/'
	if (!child) return parent
	return `${parent.replace(/\/$/, '')}/${child.replace(/^\//, '')}`
}

export function flattenStorefrontRoutes(routes = storefrontRoutes, parentPath = '') {
	const flat = []
	for (const route of routes) {
		const fullPattern = joinRoutePath(parentPath, route.path)
		flat.push({
			fullPattern,
			redirect: route.redirect || null,
			catchAll: !!route.catchAll,
		})
		if (route.children) flat.push(...flattenStorefrontRoutes(route.children, fullPattern))
	}
	return flat
}

function escapeLiteral(char) {
	return /[.*+?^${}()|[\]\\]/.test(char) ? `\\${char}` : char
}

// Enough of Vue Router 4's path syntax for this route table: :param, custom
// regex groups, and ?/*/+ quantifiers. Matching is case-insensitive because
// createRouter() leaves `sensitive` at its default of false.
export function compileRoutePattern(pattern) {
	let source = ''
	let i = 0
	while (i < pattern.length) {
		if (pattern[i] === ':' && /[A-Za-z_]/.test(pattern[i + 1] || '')) {
			i += 1
			while (i < pattern.length && /[A-Za-z0-9_]/.test(pattern[i])) i += 1
			let body = '[^/]+'
			if (pattern[i] === '(') {
				const start = i
				let depth = 0
				do {
					if (pattern[i] === '(') depth += 1
					else if (pattern[i] === ')') depth -= 1
					i += 1
				} while (i < pattern.length && depth > 0)
				body = pattern.slice(start + 1, i - 1) || '[^/]+'
			}
			let quantifier = ''
			if (pattern[i] === '?' || pattern[i] === '*' || pattern[i] === '+') {
				quantifier = pattern[i]
				i += 1
			}
			source += quantifier ? `(?:${body})${quantifier}` : `(?:${body})`
			continue
		}
		source += escapeLiteral(pattern[i])
		i += 1
	}
	return new RegExp(`^${source}$`, 'i')
}

function specificity(fullPattern) {
	const params = (fullPattern.match(/:[A-Za-z_]/g) || []).length
	const staticLength = fullPattern.replace(/:[A-Za-z0-9_]+(\([^)]*\))?[*+?]?/g, '').length
	return staticLength * 10 - params
}

let compiledRoutes

function codedRoutes() {
	if (compiledRoutes) return compiledRoutes
	compiledRoutes = flattenStorefrontRoutes()
		.filter((route) => !route.catchAll)
		.map((route) => ({
			...route,
			regex: compileRoutePattern(route.fullPattern),
			specificity: specificity(route.fullPattern),
		}))
	return compiledRoutes
}

export function findCodedRouteConflict(path) {
	if (typeof path !== 'string' || !path.startsWith('/')) return null
	const candidate = path.length > 1 ? path.replace(/\/+$/, '') : path
	let best = null
	for (const route of codedRoutes()) {
		if (!route.regex.test(candidate)) continue
		if (!best || route.specificity > best.specificity) best = route
	}
	if (!best) return null
	return { fullPattern: best.fullPattern, redirect: !!best.redirect }
}

export function codedRouteConflictMessage(conflict) {
	if (!conflict) return ''
	if (conflict.redirect) {
		return `This route is already used by the storefront (${conflict.fullPattern}), which redirects elsewhere. Pick a different route.`
	}
	return `This route is already used by the storefront (${conflict.fullPattern}). Pick a different route.`
}
